import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faTimes,
  faChevronUp,
  faChevronDown,
  faCircle,
  faStar,
  faLayerGroup,
  faCheckSquare,
} from '@fortawesome/free-solid-svg-icons'
import { useAccountContext } from '../contexts/AccountContext'
import { useAppContext } from '../contexts/AppContext'
import { useCompareToggle } from '../hooks/useCompareToggle'
import { FEATURE_FLAGS } from '../utils/featureFlags'
import { MAX_COMPARE_DEVICES } from '../common/constants'
import { DeviceInfo } from '../common/enums'
import { EmptyState } from '../shared/components/ui'
import { getStatusColorClass, DeviceStatus } from '../utils/deviceStatus'
import { useDeviceStatuses } from '../hooks/useDeviceStatuses'
import { useDevicePriorities, organizeDevicesByPriorityAndStatus } from '../hooks/useDevicePriorities'
import { LAYOUT_DIMENSIONS } from '../hooks/useResponsiveLayout'
import { Logo } from './Logo'
import { Switch } from './Switch'

// View mode types
type ViewMode = 'status' | 'name' | 'site'
type SortDirection = 'asc' | 'desc'

const MOBILE_VIEW_MODE_KEY = 'mobile_sidebar_view_mode'

interface MobileDeviceDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function MobileDeviceDrawer({ isOpen, onClose }: MobileDeviceDrawerProps) {
  const { devices, selectedDevice, selectDeviceLocally, isLoading } = useAccountContext()
  const { compareMode, selectedDevicesForComparison, toggleDeviceForComparison } = useAppContext()
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(MOBILE_VIEW_MODE_KEY)
    return (saved === 'status' || saved === 'name' || saved === 'site') ? saved : 'status'
  })
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const navigate = useNavigate()
  const location = useLocation()

  // Compare mode: the switch lives in this drawer (where devices are picked),
  // mirroring the desktop sidebar. Only relevant on the charts/compare views.
  const toggleCompareMode = useCompareToggle()
  const showCompareToggle =
    FEATURE_FLAGS.COMPARE_SENSORS &&
    (location.pathname === '/charts' || location.pathname === '/compare')
  const isAtCompareLimit = selectedDevicesForComparison.length >= MAX_COMPARE_DEVICES
  const handleToggleCompareMode = () => {
    const params = new URLSearchParams(location.search)
    const fromParam = params.get('from')
    const toParam = params.get('to')
    toggleCompareMode({
      sigfoxId: selectedDevice || undefined,
      timeRange: {
        from: fromParam ? Number(fromParam) * 1000 : undefined,
        to: toParam ? Number(toParam) * 1000 : undefined,
      },
    })
  }

  const drawerRef = useRef<HTMLDivElement>(null)
  const touchStartX = useRef<number>(0)
  const touchCurrentX = useRef<number>(0)

  // Persist view mode
  useEffect(() => {
    localStorage.setItem(MOBILE_VIEW_MODE_KEY, viewMode)
  }, [viewMode])

  // Convert devices object to array for filtering
  const devicesArray = useMemo(() => {
    return Object.values(devices)
  }, [devices])

  // Fetch device statuses
  const { deviceStatuses } = useDeviceStatuses(devicesArray)

  // Use device priorities hook
  const { enhanceDevicesWithPriority, toggleDevicePriority, priorityDeviceIds } = useDevicePriorities()

  // Filter devices by search query
  const filteredDevices = useMemo(() => {
    if (!searchQuery.trim()) return devicesArray

    const query = searchQuery.toLowerCase()
    return devicesArray.filter(
      (device) =>
        device.deviceName?.toLowerCase().includes(query) ||
        device.siteName?.toLowerCase().includes(query) ||
        device.sigfoxId?.toLowerCase().includes(query) ||
        device.wisolId?.toLowerCase().includes(query) ||
        device.siteCode?.toString().toLowerCase().includes(query)
    )
  }, [devicesArray, searchQuery])

  // Enhance devices with priority information and organize by priority and status
  const organizedDevices = useMemo(() => {
    const enhancedDevices = enhanceDevicesWithPriority(filteredDevices)
    return organizeDevicesByPriorityAndStatus(enhancedDevices, deviceStatuses)
  }, [filteredDevices, deviceStatuses, enhanceDevicesWithPriority])

  // Sort devices for flat list views (name or site)
  const sortedFlatDevices = useMemo(() => {
    if (viewMode === 'status') return []

    const enhancedDevices = enhanceDevicesWithPriority(filteredDevices)
    const sorted = [...enhancedDevices].sort((a, b) => {
      let aValue: string
      let bValue: string

      if (viewMode === 'name') {
        aValue = a.deviceName?.toLowerCase() || ''
        bValue = b.deviceName?.toLowerCase() || ''
      } else {
        aValue = a.siteName?.toLowerCase() || ''
        bValue = b.siteName?.toLowerCase() || ''
      }

      const comparison = aValue.localeCompare(bValue)
      return sortDirection === 'asc' ? comparison : -comparison
    })

    return sorted
  }, [filteredDevices, viewMode, sortDirection, enhanceDevicesWithPriority])

  // Toggle sort direction when clicking on the active column
  const handleColumnSort = (column: 'name' | 'site') => {
    if (viewMode === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setViewMode(column)
      setSortDirection('asc')
    }
  }

  const totalDevices = devicesArray.length
  const totalOnline = organizedDevices.priorityOnline.length + organizedDevices.regularOnline.length
  const totalOffline = organizedDevices.priorityOffline.length + organizedDevices.regularOffline.length
  const totalPriority = priorityDeviceIds.length

  const clearSearch = () => {
    setSearchQuery('')
  }

  const handleDeviceClick = useCallback((device: DeviceInfo) => {
    // In compare mode, tapping a row toggles it in/out of the comparison set.
    // Keep the drawer open so several devices can be picked in one pass.
    if (compareMode) {
      toggleDeviceForComparison(device.sigfoxId)
      return
    }

    selectDeviceLocally(device.sigfoxId)

    // If clicking on already selected device, toggle between /map and /charts
    if (device.sigfoxId === selectedDevice) {
      if (location.pathname === '/map' || location.pathname === '/') {
        navigate(`/charts?sigfoxId=${device.sigfoxId}`, { replace: true })
      } else if (location.pathname === '/charts') {
        navigate(`/map?sigfoxId=${device.sigfoxId}`, { replace: true })
      } else {
        navigate(`${location.pathname}?sigfoxId=${device.sigfoxId}`, { replace: true })
      }
    } else {
      // New device selected - navigate to /map by default
      const targetPath = location.pathname === '/' ? '/map' : location.pathname
      navigate(`${targetPath}?sigfoxId=${device.sigfoxId}`, { replace: true })
    }

    // Auto-close drawer after selection
    onClose()
  }, [compareMode, toggleDeviceForComparison, selectDeviceLocally, selectedDevice, location.pathname, navigate, onClose])

  // Handle swipe to close gesture
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchCurrentX.current = e.touches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    touchCurrentX.current = e.touches[0].clientX
    const deltaX = touchCurrentX.current - touchStartX.current

    // Only allow swipe left (negative delta) to close
    if (deltaX < 0 && drawerRef.current) {
      drawerRef.current.style.transform = `translateX(${deltaX}px)`
    }
  }

  const handleTouchEnd = () => {
    const deltaX = touchCurrentX.current - touchStartX.current

    if (drawerRef.current) {
      // If swiped more than 100px left, close the drawer
      if (deltaX < -100) {
        onClose()
      }
      // Reset transform
      drawerRef.current.style.transform = ''
    }
  }

  // Close drawer on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`fixed top-0 left-0 h-full bg-white shadow-xl z-50 flex flex-col transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{
          width: `min(85vw, ${LAYOUT_DIMENSIONS.MOBILE_DRAWER_MAX_WIDTH}px)`,
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Device list"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header with Logo */}
        <div className="px-3 py-2 border-b border-gray-200 flex-shrink-0">
          {/* Logo */}
          <div className="mb-2">
            <Logo shrink={true} />
          </div>

          {/* Search + Close button */}
          <div className="flex items-center gap-2 mb-2">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search devices..."
                className="w-full pl-10 pr-8 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                style={{ fontSize: '16px' }} // Prevents iOS zoom
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center touch-manipulation"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors touch-manipulation flex-shrink-0"
              aria-label="Close device list"
            >
              <FontAwesomeIcon icon={faTimes} className="text-gray-600" />
            </button>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">
              {compareMode ? (
                <>
                  <span className="font-semibold text-blue-700">{selectedDevicesForComparison.length}</span>
                  <span className="text-blue-500">/{MAX_COMPARE_DEVICES}</span>
                  <span> selected</span>
                </>
              ) : (
                <>{filteredDevices.length} of {totalDevices} devices</>
              )}
            </span>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-1">
                <FontAwesomeIcon icon={faStar} className="text-amber-500 text-xs" />
                <span className="text-gray-600">{totalPriority}</span>
              </div>
              <div className="flex items-center space-x-1">
                <FontAwesomeIcon icon={faCircle} className="text-green-500 text-xs" />
                <span className="text-gray-600">{totalOnline}</span>
              </div>
              <div className="flex items-center space-x-1">
                <FontAwesomeIcon icon={faCircle} className="text-red-500 text-xs" />
                <span className="text-gray-600">{totalOffline}</span>
              </div>
            </div>
          </div>

          {/* View Switcher */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => handleColumnSort('name')}
              className={`flex-1 px-2 py-2 text-sm font-medium rounded-md transition-all touch-manipulation ${
                viewMode === 'name'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Name
            </button>
            <button
              onClick={() => handleColumnSort('site')}
              className={`flex-1 px-2 py-2 text-sm font-medium rounded-md transition-all touch-manipulation ${
                viewMode === 'site'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Site
            </button>
            <button
              onClick={() => setViewMode('status')}
              className={`flex-1 px-2 py-2 text-sm font-medium rounded-md transition-all touch-manipulation ${
                viewMode === 'status'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Status
            </button>
          </div>
        </div>

        {/* Device List */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
              <p className="text-sm">Loading devices...</p>
            </div>
          ) : filteredDevices.length === 0 ? (
            <EmptyState
              variant={searchQuery ? 'search' : 'default'}
              title={searchQuery ? 'No devices found' : 'No devices'}
              description={searchQuery ? 'Try adjusting your search terms' : 'No devices are currently available'}
              className="py-8"
            />
          ) : viewMode === 'status' ? (
            /* Grouped View (By Status) */
            <div className="p-2">
              {/* Priority Online Devices */}
              {organizedDevices.priorityOnline.length > 0 && (
                <div className="mb-3">
                  <h3 className="text-xs font-medium text-amber-600 uppercase tracking-wide px-2 mb-1 flex items-center space-x-1.5">
                    <FontAwesomeIcon icon={faStar} className="text-amber-500 text-xs" />
                    <span>Priority Online ({organizedDevices.priorityOnline.length})</span>
                  </h3>
                  <div>
                    {organizedDevices.priorityOnline.map((device) => (
                      <MobileDeviceRow
                        key={device.deviceId}
                        device={device}
                        isSelected={device.sigfoxId === selectedDevice}
                        onClick={() => handleDeviceClick(device)}
                        status={deviceStatuses[device.sigfoxId]?.status || 'pending'}
                        compareMode={compareMode}
                        isSelectedForCompare={selectedDevicesForComparison.includes(device.sigfoxId)}
                        isAtCompareLimit={isAtCompareLimit}
                        onTogglePriority={toggleDevicePriority}
                        isPriority={true}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Priority Offline Devices */}
              {organizedDevices.priorityOffline.length > 0 && (
                <div className="mb-3">
                  <h3 className="text-xs font-medium text-amber-600 uppercase tracking-wide px-2 mb-1 flex items-center space-x-1.5">
                    <FontAwesomeIcon icon={faStar} className="text-amber-500 text-xs" />
                    <span>Priority Offline ({organizedDevices.priorityOffline.length})</span>
                  </h3>
                  <div>
                    {organizedDevices.priorityOffline.map((device) => (
                      <MobileDeviceRow
                        key={device.deviceId}
                        device={device}
                        isSelected={device.sigfoxId === selectedDevice}
                        onClick={() => handleDeviceClick(device)}
                        status={deviceStatuses[device.sigfoxId]?.status || 'pending'}
                        compareMode={compareMode}
                        isSelectedForCompare={selectedDevicesForComparison.includes(device.sigfoxId)}
                        isAtCompareLimit={isAtCompareLimit}
                        onTogglePriority={toggleDevicePriority}
                        isPriority={true}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Online Devices */}
              {organizedDevices.regularOnline.length > 0 && (
                <div className="mb-3">
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 mb-1">
                    Online ({organizedDevices.regularOnline.length})
                  </h3>
                  <div>
                    {organizedDevices.regularOnline.map((device) => (
                      <MobileDeviceRow
                        key={device.deviceId}
                        device={device}
                        isSelected={device.sigfoxId === selectedDevice}
                        onClick={() => handleDeviceClick(device)}
                        status={deviceStatuses[device.sigfoxId]?.status || 'pending'}
                        compareMode={compareMode}
                        isSelectedForCompare={selectedDevicesForComparison.includes(device.sigfoxId)}
                        isAtCompareLimit={isAtCompareLimit}
                        onTogglePriority={toggleDevicePriority}
                        isPriority={false}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Offline Devices */}
              {organizedDevices.regularOffline.length > 0 && (
                <div>
                  <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 mb-1">
                    Offline ({organizedDevices.regularOffline.length})
                  </h3>
                  <div>
                    {organizedDevices.regularOffline.map((device) => (
                      <MobileDeviceRow
                        key={device.deviceId}
                        device={device}
                        isSelected={device.sigfoxId === selectedDevice}
                        onClick={() => handleDeviceClick(device)}
                        status={deviceStatuses[device.sigfoxId]?.status || 'pending'}
                        compareMode={compareMode}
                        isSelectedForCompare={selectedDevicesForComparison.includes(device.sigfoxId)}
                        isAtCompareLimit={isAtCompareLimit}
                        onTogglePriority={toggleDevicePriority}
                        isPriority={false}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Flat Sorted View (By Name or By Site) */
            <div>
              {/* Column Headers */}
              <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-2 py-2 flex items-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                <div className="w-6 flex-shrink-0" /> {/* Status dot spacer */}
                <button
                  onClick={() => handleColumnSort('name')}
                  className={`w-[110px] flex-shrink-0 flex items-center space-x-1 hover:text-gray-700 touch-manipulation ${viewMode === 'name' ? 'text-gray-900' : ''}`}
                >
                  <span>Name</span>
                  {viewMode === 'name' && (
                    <FontAwesomeIcon
                      icon={sortDirection === 'asc' ? faChevronUp : faChevronDown}
                      className="text-xs"
                    />
                  )}
                </button>
                <button
                  onClick={() => handleColumnSort('site')}
                  className={`flex-1 flex items-center space-x-1 hover:text-gray-700 touch-manipulation ${viewMode === 'site' ? 'text-gray-900' : ''}`}
                >
                  <span>Site</span>
                  {viewMode === 'site' && (
                    <FontAwesomeIcon
                      icon={sortDirection === 'asc' ? faChevronUp : faChevronDown}
                      className="text-xs"
                    />
                  )}
                </button>
                <div className="w-10 flex-shrink-0" /> {/* Star spacer */}
              </div>

              {/* Flat Device List */}
              <div className="p-2">
                {sortedFlatDevices.map((device) => (
                  <MobileDeviceRow
                    key={device.deviceId}
                    device={device}
                    isSelected={device.sigfoxId === selectedDevice}
                    onClick={() => handleDeviceClick(device)}
                    status={deviceStatuses[device.sigfoxId]?.status || 'pending'}
                    compareMode={compareMode}
                    isSelectedForCompare={selectedDevicesForComparison.includes(device.sigfoxId)}
                    isAtCompareLimit={isAtCompareLimit}
                    onTogglePriority={toggleDevicePriority}
                    isPriority={priorityDeviceIds.includes(device.sigfoxId)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Compare Mode switch — pinned to the bottom, next to device selection */}
        {showCompareToggle && (
          <div className="flex-shrink-0 border-t border-gray-200 p-2">
            <div
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${
                compareMode ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'
              }`}
            >
              <FontAwesomeIcon
                icon={faLayerGroup}
                className={`shrink-0 ${compareMode ? 'text-purple-600' : 'text-gray-400'}`}
              />
              <Switch
                label="Compare devices"
                state={compareMode}
                onChange={handleToggleCompareMode}
                labelClassName={compareMode ? 'text-purple-700 font-medium' : 'text-gray-700'}
              />
            </div>
          </div>
        )}

        {/* Swipe indicator */}
        <div className="flex-shrink-0 py-2 border-t border-gray-100">
          <p className="text-xs text-center text-gray-400">Swipe left to close</p>
        </div>
      </div>
    </>
  )
}

interface MobileDeviceRowProps {
  device: DeviceInfo
  isSelected: boolean
  onClick: () => void
  status: DeviceStatus
  onTogglePriority: (deviceId: string, isPriority: boolean) => Promise<void>
  isPriority: boolean
  // Compare mode props
  compareMode?: boolean
  isSelectedForCompare?: boolean
  isAtCompareLimit?: boolean
}

function MobileDeviceRow({
  device,
  isSelected,
  onClick,
  status,
  onTogglePriority,
  isPriority,
  compareMode = false,
  isSelectedForCompare = false,
  isAtCompareLimit = false,
}: MobileDeviceRowProps) {
  const statusColorClass = getStatusColorClass(status)

  // In compare mode a row can only be tapped if it's already selected (to
  // deselect) or the 10-device cap hasn't been reached yet.
  const canSelect = !isAtCompareLimit || isSelectedForCompare
  const interactive = !compareMode || canSelect

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await onTogglePriority(device.sigfoxId, !isPriority)
  }

  const getRowClassName = () => {
    const base = 'w-full min-h-[48px] px-2 py-2 flex items-center transition-colors rounded-lg touch-manipulation'
    if (compareMode) {
      if (isSelectedForCompare) return `${base} cursor-pointer bg-blue-50 border-l-4 border-blue-500`
      if (!canSelect) return `${base} opacity-50 cursor-not-allowed bg-gray-50`
      return `${base} cursor-pointer hover:bg-blue-50/50 active:bg-blue-100`
    }
    if (isSelected) return `${base} cursor-pointer ${isPriority ? 'bg-amber-100' : 'bg-blue-50'}`
    return `${base} cursor-pointer ${isPriority ? 'bg-amber-50/50 hover:bg-amber-50 active:bg-amber-100' : 'hover:bg-gray-50 active:bg-gray-100'}`
  }

  return (
    <div
      onClick={interactive ? onClick : undefined}
      role="button"
      tabIndex={interactive ? 0 : -1}
      onKeyDown={(e) => {
        if (interactive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClick()
        }
      }}
      className={getRowClassName()}
      title={!canSelect && compareMode ? `Maximum ${MAX_COMPARE_DEVICES} devices selected` : undefined}
    >
      {/* Compare mode checkbox */}
      {compareMode && (
        <div className="w-8 flex-shrink-0 flex items-center justify-center">
          {isSelectedForCompare ? (
            <FontAwesomeIcon icon={faCheckSquare} className="text-lg text-blue-600" />
          ) : (
            <span
              className={`w-5 h-5 border-2 rounded-sm ${
                canSelect ? 'border-gray-400 bg-white' : 'border-gray-300 bg-gray-100'
              }`}
            />
          )}
        </div>
      )}

      {/* Status dot */}
      <div className="w-6 flex-shrink-0 flex items-center justify-center">
        <FontAwesomeIcon icon={faCircle} className={`text-[10px] ${statusColorClass}`} />
      </div>

      {/* Device name */}
      <div className="w-[110px] flex-shrink-0 pr-2">
        <span className="text-sm font-medium text-gray-900 truncate block">
          {device.deviceName || 'Unnamed'}
        </span>
      </div>

      {/* Site name */}
      <div className="flex-1 min-w-0 pr-2">
        <span className="text-sm text-gray-500 truncate block">
          {device.siteName || '-'}
        </span>
      </div>

      {/* Favorite star - hidden in compare mode */}
      {!compareMode && (
        <button
          onClick={handleFavoriteClick}
          className="w-10 h-10 flex-shrink-0 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 rounded-full transition-colors touch-manipulation"
          aria-label={isPriority ? 'Remove from favorites' : 'Add to favorites'}
        >
          <FontAwesomeIcon
            icon={faStar}
            className={`text-sm ${isPriority ? 'text-amber-500' : 'text-gray-300'}`}
          />
        </button>
      )}
    </div>
  )
}
